import { KanbanAggregations } from "@/pages/deals/components/KanbanBoard/types";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { AggregationField, GenericAggregations } from "@/services/api/types";
import { daysToWeeks } from "date-fns";
import { capitalize, has, isEmpty, sumBy } from "lodash";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import BottomKPIBar, {
  KPI
} from "@/components/controls/BottomKPIBar/BottomKPIBar";

interface DealsKPIData extends Record<string, number> {
  total: number;
  arrMrr: number;
  avgArrMrr: number;
  avgTime: number;
  committedArrMrr: number;
}

interface Props {
  aggregations: KanbanAggregations | GenericAggregations<string> | {};
  totalDeals?: number;
  isLoading?: boolean;
  isError?: boolean;
  isRowsError?: boolean;
  isHidden?: boolean;
  onOpenStateChange?: (isOpen: boolean) => void;
}

const DealsBottomKPIBar: React.FC<Props> = ({
  aggregations,
  totalDeals,
  isLoading = false,
  isError = false,
  isRowsError = false,
  isHidden = false,
  onOpenStateChange
}) => {
  const { t } = useTranslation();
  const { fieldName: arrOrMrrField, isLoading: arrMrrLoading } = useArrMrr();

  // The two layouts pass different aggregation shapes: the table a single
  // GenericAggregations with a `data` array, the kanban a per-stage map. Keying
  // off the deal count instead would route a table whose rows request failed
  // - total 0 - down the kanban branch, which finds no stages and reports every
  // money tile as a confident zero.
  const isKanbanLayout = !has(aggregations, "data");

  // Withhold the basis-dependent tiles until the basis is known. The deal count
  // comes from the rows request in both layouts - directly in the table, and as
  // the sum of the per-stage row totals on the kanban board - so it follows
  // that request's failure either way, not the aggregations'.
  const kpis: KPI<DealsKPIData>[] = [
    { name: "total", isRowsDerived: true },
    ...(arrMrrLoading
      ? []
      : [
          { name: "arrMrr" as const, isMonetary: true },
          { name: "committedArrMrr" as const, isMonetary: true },
          {
            name: "avgArrMrr" as const,
            isMonetary: true,
            needsBothSources: true
          }
        ]),
    {
      name: "avgTime",
      unit: t("deals.bottomKPI.avgTimeUnits"),
      needsBothSources: true
    }
  ];

  const getAllFieldsFromData = (fieldName: string) => {
    if (isEmpty(aggregations)) return [];
    if (isKanbanLayout) {
      const allFields: AggregationField<string>[] = [];
      Object.values(aggregations as KanbanAggregations).forEach(
        (stageAggregation) => {
          if (stageAggregation && stageAggregation?.aggregations?.data) {
            const stageFields = stageAggregation?.aggregations.data
              .map((item) =>
                item.fields.find(
                  (field: AggregationField<string>) => field.name === fieldName
                )
              )
              .filter(
                (field): field is AggregationField<string> =>
                  field !== undefined
              );

            allFields.push(...stageFields);
          }
        }
      );

      return allFields;
    }
    // table layout
    if (!has(aggregations, "data")) return [];
    return (aggregations as GenericAggregations<string>).data
      .map((item) =>
        item.fields.find(
          (field: AggregationField<string>) => field.name === fieldName
        )
      )
      .filter((field) => field !== undefined);
  };

  const getTotalDeals = () => {
    if (isEmpty(aggregations)) return 0;
    let numOfDeals = 0;
    Object.values(aggregations as KanbanAggregations).forEach(
      (stageAggregation) => {
        if (stageAggregation && stageAggregation?.total) {
          numOfDeals += stageAggregation?.total;
        }
      }
    );

    return numOfDeals;
  };

  const data: DealsKPIData = useMemo(() => {
    let arrMrr = 0;
    let committedArrMrr = 0;
    let avgArrMrr = 0;
    let avgTime = 0;
    const numOfDeals = totalDeals ? totalDeals : getTotalDeals();

    if (!isEmpty(aggregations)) {
      // Get all fields for each aggregation across all stages
      const arrMrrFields = getAllFieldsFromData(arrOrMrrField);
      const openForDaysFields = getAllFieldsFromData("openForDays");
      const committedArrMrrFields = getAllFieldsFromData(
        `committed${capitalize(arrOrMrrField)}`
      );

      arrMrr = sumBy(arrMrrFields, (field) => field?.aggregations.sum || 0);
      committedArrMrr = sumBy(
        committedArrMrrFields,
        (field) => field?.aggregations.sum || 0
      );

      // Calculate averages across all items
      const totalOpenDays = sumBy(
        openForDaysFields,
        (field) => field?.aggregations.sum || 0
      );

      // Aggregations can come back populated over an empty deal set, so the
      // averages divide by a real count or report nothing to average — not a
      // NaN the bar renders as a confident zero.
      avgArrMrr = numOfDeals > 0 ? arrMrr / numOfDeals : 0;

      avgTime = numOfDeals > 0 ? daysToWeeks(totalOpenDays / numOfDeals) : 0;
    }

    return {
      total: numOfDeals,
      arrMrr,
      committedArrMrr,
      avgArrMrr,
      avgTime
    };
  }, [aggregations, totalDeals, arrOrMrrField]);

  return (
    <BottomKPIBar<DealsKPIData>
      kpis={kpis}
      data={data}
      pageName="deals"
      isLoading={isLoading}
      isError={isError}
      isRowsError={isRowsError}
      isHidden={isHidden}
      onOpenStateChange={onOpenStateChange}
    />
  );
};

export default DealsBottomKPIBar;
